import Editor from '@monaco-editor/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useSession } from '../auth/session';

export function EditPage() {
  const { user } = useSession();
  const [value, setValue] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'ok' | 'err'>('idle');
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/spec', { credentials: 'include' });
        if (!res.ok) {
          setLoadError(`Could not load spec (${res.status}).`);
          return;
        }
        const text = await res.text();
        if (!cancelled) setValue(text);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Load failed.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setSaveState('saving');
    setSaveMessage('');
    try {
      const res = await fetch('/api/spec', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'text/yaml; charset=utf-8' },
        body: value,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveState('err');
        setSaveMessage(
          typeof data.message === 'string' ? data.message : data.error || res.statusText,
        );
        return;
      }
      setSaveState('ok');
      setSaveMessage(`Saved (${data.bytes ?? value.length} bytes).`);
    } catch (e) {
      setSaveState('err');
      setSaveMessage(e instanceof Error ? e.message : 'Save failed.');
    }
  }, [value]);

  if (!user) {
    return (
      <div className="panel">
        <h1>Edit API specification</h1>
        <p>Sign in with your MyCC account to edit the OpenAPI document.</p>
        <p>
          <a className="btn primary" href={`/api/auth/login?next=${encodeURIComponent('/edit')}`}>
            Sign in
          </a>
        </p>
        <p>
          <Link to="/">← Back to documentation</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="edit-wrap">
      <div className="edit-toolbar">
        <Link to="/" className="back">
          ← View docs
        </Link>
        <div className="grow" />
        {saveState === 'ok' && <span className="ok-msg">{saveMessage}</span>}
        {(saveState === 'err' || saveState === 'idle') && saveMessage && (
          <span className="err-msg">{saveMessage}</span>
        )}
        <button type="button" className="btn primary" disabled={saveState === 'saving'} onClick={() => void save()}>
          {saveState === 'saving' ? 'Validating…' : 'Validate & save'}
        </button>
      </div>
      {loadError ? (
        <p className="panel err-msg">{loadError}</p>
      ) : (
        <Editor
          height="calc(100vh - 132px)"
          defaultLanguage="yaml"
          theme="vs-dark"
          value={value}
          onChange={(v) => setValue(v ?? '')}
          options={{
            minimap: { enabled: true },
            wordWrap: 'on',
            fontSize: 13,
            tabSize: 2,
          }}
        />
      )}
    </div>
  );
}
